import React, { useEffect, useRef, useState } from "react";
import { Radio, RefreshCw, Users, Car, AlertTriangle } from "lucide-react";
import { api } from "../api/client";
import { zoneColorFor } from "./TariffsView";

export default function LiveMapView() {
  const [data, setData] = useState({ drivers: [], rides: [], zones: [], sos: [] });
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const mapRef = useRef(null);
  const leafletMap = useRef(null);
  const layers = useRef({ drivers: null, rides: null, zones: null, sos: null });

  // Cargar Leaflet dinámicamente
  useEffect(() => {
    if (window.L) return initMap();
    const css = document.createElement("link");
    css.rel = "stylesheet";
    css.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
    document.head.appendChild(css);
    const s = document.createElement("script");
    s.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
    s.onload = initMap;
    document.body.appendChild(s);
  }, []);

  const initMap = () => {
    if (leafletMap.current || !mapRef.current || !window.L) return;
    const L = window.L;
    // Centro Puerto Maldonado
    leafletMap.current = L.map(mapRef.current, { zoomControl: true, attributionControl: false })
      .setView([-12.5933, -69.1893], 13);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 19 }).addTo(leafletMap.current);
    // Attribution mínima
    L.control.attribution({ prefix: false }).addAttribution("© OpenStreetMap").addTo(leafletMap.current);
    layers.current.drivers = L.layerGroup().addTo(leafletMap.current);
    layers.current.rides = L.layerGroup().addTo(leafletMap.current);
    layers.current.zones = L.layerGroup().addTo(leafletMap.current);
    layers.current.sos = L.layerGroup().addTo(leafletMap.current);
    load();
  };

  const load = async () => {
    try {
      setLoading(true);
      const res = await api.get("/live/operations");
      setData(res.data);
      setLastUpdate(new Date());
      renderMap(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, []);

  const renderMap = (d) => {
    if (!window.L || !leafletMap.current) return;
    const L = window.L;
    // Limpiar capas
    Object.values(layers.current).forEach(lg => lg && lg.clearLayers());

    // ZONAS con polígono. Color determinístico por zone.id — la misma zona
    // se ve del mismo color en TariffsView (paleta compartida).
    d.zones.forEach(z => {
      const color = zoneColorFor(z.id || z.name);
      if (z.polygon && z.polygon.length >= 3) {
        L.polygon(z.polygon, {
          color, weight: 2, fillColor: color, fillOpacity: 0.18,
        }).bindTooltip(z.name, { permanent: false }).addTo(layers.current.zones);
      } else if (z.lat && z.lng) {
        L.circle([z.lat, z.lng], { radius: 300, color, weight: 1, fillColor: color, fillOpacity: 0.15 })
          .bindTooltip(z.name).addTo(layers.current.zones);
      }
    });

    // CONDUCTORES (color según status). Drivers online SIN GPS aún cuentan en
    // las estadísticas de la parte inferior, pero NO se dibujan en el mapa
    // porque no tenemos dónde ubicarlos hasta que reporten su primera posición.
    d.drivers.forEach(dr => {
      if (typeof dr.last_lat !== "number" || typeof dr.last_lng !== "number") return;
      const color = dr.driver_status === "busy" ? "#F59E0B" : dr.driver_status === "available" ? "#10B981" : "#94A3B8";
      const marker = L.divIcon({
        className: "live-driver-marker",
        html: `<div style="background:${color};border:3px solid #fff;width:16px;height:16px;border-radius:50%;box-shadow:0 0 10px ${color}80;position:relative;">
          <div style="position:absolute;top:-4px;left:-4px;right:-4px;bottom:-4px;border-radius:50%;background:${color}40;animation:pulse-driver 2s infinite;"></div>
        </div>`,
        iconSize: [16, 16],
      });
      L.marker([dr.last_lat, dr.last_lng], { icon: marker })
        .bindTooltip(`<b>${dr.name}</b><br>${dr.vehicle_plate || 'Sin placa'} · ${dr.driver_status || 'offline'}${dr.current_ride_id ? '<br>🚕 En carrera' : ''}`, { direction: "top" })
        .addTo(layers.current.drivers);
    });

    // CARRERAS (líneas + pines)
    d.rides.forEach(r => {
      if (r.origin_lat && r.destination_lat) {
        const color = r.status === "pending" ? "#F59E0B" : r.status === "in_progress" ? "#10B981" : "#3B82F6";
        L.polyline([[r.origin_lat, r.origin_lng], [r.destination_lat, r.destination_lng]], {
          color, weight: 3, opacity: 0.8, dashArray: r.status === "pending" ? "8, 6" : null,
        }).bindTooltip(`<b>${r.passenger_name}</b><br>${r.origin_zone_name} → ${r.destination_zone_name}<br>${r.status.toUpperCase()} · S/${r.estimated_fare}`, { sticky: true }).addTo(layers.current.rides);
      }
    });

    // SOS (marker rojo pulsante grande)
    d.sos.forEach(s => {
      if (s.lat && s.lng) {
        const sosIcon = L.divIcon({
          className: "live-sos-marker",
          html: `<div style="background:#EF4444;border:4px solid #fff;width:26px;height:26px;border-radius:50%;box-shadow:0 0 20px #EF4444;position:relative;font-size:14px;text-align:center;line-height:22px;">⚠</div>
          <div style="position:absolute;top:-15px;left:-15px;width:56px;height:56px;border-radius:50%;background:#EF444450;animation:pulse-driver 1s infinite;"></div>`,
          iconSize: [26, 26],
        });
        L.marker([s.lat, s.lng], { icon: sosIcon, zIndexOffset: 1000 })
          .bindTooltip(`<b>🚨 SOS · ${s.type.toUpperCase()}</b><br>${s.driver_name}<br>${s.driver_phone}${s.notes ? '<br>' + s.notes : ''}`, { direction: "top", permanent: true, className: "sos-tooltip" })
          .addTo(layers.current.sos);
      }
    });
  };

  const stats = {
    online: data.drivers.filter(d => d.is_online).length,
    busy: data.drivers.filter(d => d.driver_status === "busy").length,
    available: data.drivers.filter(d => d.driver_status === "available").length,
    pending: data.rides.filter(r => r.status === "pending").length,
    active: data.rides.filter(r => ["assigned", "in_progress"].includes(r.status)).length,
    sos: data.sos.length,
  };

  return (
    <div className="fade-in flex flex-col" style={{ height: "calc(100vh - 130px)" }} data-testid="live-map-view">
      <style>{`
        @keyframes pulse-driver {
          0% { transform: scale(0.8); opacity: 1; }
          100% { transform: scale(2.2); opacity: 0; }
        }
        .sos-tooltip { background: #7F1D1D !important; color: #fff !important; font-weight:600; border: none !important; box-shadow: 0 4px 12px rgba(0,0,0,0.4) !important; }
      `}</style>
      <div className="flex justify-between items-center mb-3">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-[#3949AB]">Operación en vivo</div>
          <h1 className="font-display font-bold text-2xl text-neutral-900 mt-0.5 flex items-center gap-2">
            <Radio className="w-6 h-6 text-red-500 animate-pulse" /> Central de Operaciones
          </h1>
        </div>
        <div className="flex items-center gap-2 text-xs">
          {lastUpdate && (
            <span className="text-neutral-500">
              Actualizado {lastUpdate.toLocaleTimeString('es-PE')}
            </span>
          )}
          <button onClick={load} className="p-2 rounded hover:bg-neutral-100" data-testid="live-refresh">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-6 gap-3 mb-3">
        <StatChip icon={Users} label="Online" value={stats.online} color="text-emerald-600" bg="bg-emerald-50" />
        <StatChip icon={Car} label="Disponibles" value={stats.available} color="text-emerald-600" bg="bg-emerald-50" />
        <StatChip icon={Car} label="Ocupados" value={stats.busy} color="text-amber-600" bg="bg-amber-50" />
        <StatChip icon={Radio} label="En curso" value={stats.active} color="text-[#3949AB]" bg="bg-indigo-50" />
        <StatChip icon={Radio} label="Pendientes" value={stats.pending} color="text-amber-600" bg="bg-amber-50" />
        <StatChip icon={AlertTriangle} label="SOS" value={stats.sos} color="text-red-600" bg="bg-red-50" pulse={stats.sos > 0} />
      </div>

      <div ref={mapRef} className="flex-1 rounded-lg border border-neutral-200 shadow-sm" style={{ minHeight: 400 }} data-testid="live-map-canvas" />

      <div className="mt-3 flex gap-3 flex-wrap text-xs text-neutral-600">
        <LegendItem color="#10B981" label="Conductor disponible" />
        <LegendItem color="#F59E0B" label="Conductor ocupado" />
        <LegendItem color="#94A3B8" label="Offline" />
        <LegendItem color="#3B82F6" label="Ruta asignada" />
        <LegendItem color="#F59E0B" label="Pendiente (línea punteada)" />
        <LegendItem color="#10B981" label="En curso" />
        <LegendItem color="#EF4444" label="SOS" />
        <LegendItem color="#3949AB" label="Zona tarifada" />
      </div>
    </div>
  );
}

function StatChip({ icon: Ic, label, value, color, bg, pulse }) {
  return (
    <div className={`p-3 rounded-lg border border-neutral-200 bg-white flex items-center gap-2 ${pulse ? "animate-pulse ring-2 ring-red-300" : ""}`}>
      <div className={`w-8 h-8 rounded ${bg} flex items-center justify-center`}>
        <Ic className={`w-4 h-4 ${color}`} />
      </div>
      <div className="min-w-0">
        <div className={`text-lg font-bold leading-tight ${color}`}>{value}</div>
        <div className="text-[10px] text-neutral-500 uppercase tracking-wider truncate">{label}</div>
      </div>
    </div>
  );
}

function LegendItem({ color, label }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="w-3 h-3 rounded-full" style={{ background: color }}></span>
      {label}
    </div>
  );
}
